import { Thermometer } from "lucide-react";
import { useLanguage } from "@/lib/LanguageContext";

const SCALE_MIN = 150;
const SCALE_MAX = 230;
const span = SCALE_MAX - SCALE_MIN;
const pct = (temp) => `${((temp - SCALE_MIN) / span) * 100}%`;

const PHASES = [
  { key: "flavor", range: [160, 180], bar: "bg-chart-2", label: { pl: "Faza aromatyczna", en: "Aromatic phase" } },
  { key: "therapeutic", range: [180, 205], bar: "bg-chart-1", label: { pl: "Złoty standard", en: "Golden standard" } },
  { key: "full", range: [205, 220], bar: "bg-chart-4", label: { pl: "Pełna ekstrakcja", en: "Full extraction" } },
  { key: "risk", range: [220, 230], bar: "bg-destructive", label: { pl: "Piroliza", en: "Pyrolysis" } },
];

const getPhase = (temp) =>
  PHASES.find((p) => temp >= p.range[0] && temp < p.range[1]) || (temp >= 220 ? PHASES[3] : PHASES[0]);

/**
 * StrainVapeCompareTable — table comparing suggested vaporization
 * temperature ranges for multiple strains, each drawn on a shared
 * 150–230°C scale so differences are immediately visible.
 *
 * @param {Array} items - [{ name, color (hex), vape: { min, max } }]
 */
export default function StrainVapeCompareTable({ items }) {
  const { language } = useLanguage();
  const isPl = language === "pl";
  const rows = (items || []).filter((r) => r && r.vape);

  if (rows.length === 0) return null;

  const scaleTicks = [150, 160, 180, 205, 220, 230];
  const title = isPl ? "Porównanie temperatur waporyzacji" : "Vaporization temperature comparison";
  const subtitle = isPl
    ? "Sugerowane zakresy na wspólnej skali — różnice w profilu waporyzacji"
    : "Suggested ranges on a shared scale — differences in vaporization profile";
  const rangeLabel = isPl ? "Zakres" : "Range";
  const phaseLabel = isPl ? "Faza" : "Phase";

  // Highest vs lowest across all strains
  const maxes = rows.map((r) => r.vape.max);
  const highest = Math.max(...maxes);
  const lowest = Math.min(...maxes);
  const diff = highest - lowest;
  const highestName = rows.find((r) => r.vape.max === highest)?.name;
  const lowestName = rows.find((r) => r.vape.max === lowest)?.name;

  return (
    <div className="bg-card rounded-xl border border-border p-4 sm:p-5 mb-6">
      <div className="flex items-center gap-2 mb-1">
        <Thermometer className="w-5 h-5 text-chart-1" />
        <h3 className="font-semibold">{title}</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-5">{subtitle}</p>

      {/* Phase legend strip on the shared scale */}
      <div className="flex h-2.5 rounded-full overflow-hidden border border-border mb-1">
        {PHASES.map((p) => (
          <div
            key={p.key}
            className={`${p.bar} opacity-50`}
            style={{ width: `${((p.range[1] - p.range[0]) / span) * 100}%` }}
            title={`${p.label[isPl ? "pl" : "en"]} ${p.range[0]}–${p.range[1]}°C`}
          />
        ))}
      </div>
      <div className="relative h-3 mb-4">
        {scaleTicks.map((t) => (
          <span
            key={t}
            className="absolute -translate-x-1/2 text-[10px] text-muted-foreground"
            style={{ left: pct(t) }}
          >
            {t}°
          </span>
        ))}
      </div>

      {/* Per-strain rows */}
      <div className="space-y-4">
        {rows.map((r) => {
          const phase = getPhase(r.vape.max);
          const leftPct = pct(r.vape.min);
          const widthPct = ((r.vape.max - r.vape.min) / span) * 100;
          return (
            <div key={r.name}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-semibold truncate" style={{ color: r.color }}>
                  {r.name}
                </span>
                <span className="text-xs text-muted-foreground">
                  {rangeLabel}: <span className="font-medium text-foreground">{r.vape.min}°C – {r.vape.max}°C</span>
                </span>
              </div>
              <div className="relative h-5 bg-muted rounded-full overflow-hidden border border-border">
                {/* range bar */}
                <div
                  className="absolute h-full rounded-full"
                  style={{ left: leftPct, width: `${widthPct}%`, backgroundColor: r.color }}
                />
              </div>
              <div className="flex items-center gap-1.5 mt-1.5">
                <span className={`w-2 h-2 rounded-full ${phase.bar}`} />
                <span className="text-[11px] text-muted-foreground">
                  {phaseLabel}: <span className="text-foreground font-medium">{phase.label[isPl ? "pl" : "en"]}</span>
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Difference summary */}
      {rows.length >= 2 && diff === 0 && (
        <p className="text-xs text-muted-foreground mt-5 pt-4 border-t border-border">
          {isPl ? "Wszystkie odmiany mają identyczny sugerowany zakres waporyzacji." : "All strains have an identical suggested vaporization range."}
        </p>
      )}
      {rows.length >= 2 && diff > 0 && (
        <p className="text-xs text-muted-foreground mt-5 pt-4 border-t border-border">
          {isPl
            ? `${highestName} wymaga o ${diff}°C wyższej temperatury dla pełnej ekstrakcji niż ${lowestName}.`
            : `${highestName} requires ${diff}°C higher temperature for full extraction than ${lowestName}.`}
        </p>
      )}
    </div>
  );
}